import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Pencil, Trash2, Wallet } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/atoms/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  usePayrollProfiles,
  useCreatePayrollProfile,
  useUpdatePayrollProfile,
  useDeletePayrollProfile,
} from '@/hooks/payroll/usePayrollProfiles';
import { useHasPermission } from '@/hooks/usePermissions';
import type { PayrollProfile } from '@/types/payrollProfile';

export const Route = createFileRoute('/_dashboard/dashboard/settings/payroll-profiles')({
  component: PayrollProfilesPage,
});

interface Draft {
  name: string;
  baseHourly: string;
  costHourly: string;
  otMultiplier: string;
  otWeeklyThreshold: string;
  commissionPct: string;
  isActive: boolean;
}

function emptyDraft(): Draft {
  return {
    name: '',
    baseHourly: '',
    costHourly: '',
    otMultiplier: '1.5',
    otWeeklyThreshold: '40',
    commissionPct: '',
    isActive: true,
  };
}

function draftFrom(p: PayrollProfile): Draft {
  return {
    name: p.name,
    baseHourly: String(p.baseHourly),
    costHourly: p.costHourly != null ? String(p.costHourly) : '',
    otMultiplier: String(p.otMultiplier),
    otWeeklyThreshold: String(p.otWeeklyThreshold),
    commissionPct: p.commissionPct != null ? String(p.commissionPct) : '',
    isActive: p.isActive,
  };
}

function PayrollProfilesPage() {
  const canManage = useHasPermission('payroll:manage');
  const { data: profiles = [], isLoading } = usePayrollProfiles();
  const create = useCreatePayrollProfile();
  const update = useUpdatePayrollProfile();
  const remove = useDeletePayrollProfile();

  const [editing, setEditing] = useState<PayrollProfile | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [deleting, setDeleting] = useState<PayrollProfile | null>(null);

  function openNew() {
    setDraft(emptyDraft());
    setEditing('new');
  }
  function openEdit(p: PayrollProfile) {
    setDraft(draftFrom(p));
    setEditing(p);
  }

  function num(v: string): number | undefined {
    return v.trim() === '' ? undefined : Number(v);
  }

  function save() {
    const base = num(draft.baseHourly);
    if (!draft.name.trim() || base === undefined) return;
    const payload = {
      name: draft.name.trim(),
      baseHourly: base,
      costHourly: num(draft.costHourly),
      otMultiplier: num(draft.otMultiplier),
      otWeeklyThreshold: num(draft.otWeeklyThreshold),
      commissionPct: num(draft.commissionPct),
      isActive: draft.isActive,
    };
    if (editing === 'new') {
      create.mutate(payload, { onSuccess: () => setEditing(null) });
    } else if (editing) {
      update.mutate({ id: editing.id, dto: payload }, { onSuccess: () => setEditing(null) });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payroll profiles</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Reusable compensation profiles assigned to technicians
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={openNew}>
            <Plus size={14} className="mr-1.5" />
            New profile
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="text-right font-semibold">Base $/hr</TableHead>
              <TableHead className="text-right font-semibold">Overtime</TableHead>
              <TableHead className="text-right font-semibold">Commission</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            ) : profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-28 text-center text-sm text-muted-foreground">
                  <Wallet className="mx-auto mb-2 h-6 w-6 opacity-50" />
                  No payroll profiles yet.
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm font-medium">{p.name}</TableCell>
                  <TableCell className="text-right text-sm">${p.baseHourly.toFixed(2)}</TableCell>
                  <TableCell className="text-right text-sm">
                    {p.otMultiplier}× after {p.otWeeklyThreshold}h
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {p.commissionPct != null ? `${p.commissionPct}%` : '—'}
                  </TableCell>
                  <TableCell>
                    {p.isActive ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManage && (
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(p)}
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={() => setDeleting(p)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit form */}
      <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>
              {editing === 'new' ? 'New payroll profile' : 'Edit payroll profile'}
            </SheetTitle>
            <SheetDescription>Rates apply to technicians assigned this profile.</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3 text-sm">
            <label className="block text-xs text-muted-foreground">
              Name
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="HVAC — Senior Tech"
                className="mt-1 h-8 text-sm"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-muted-foreground">
                Base $/hr
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.baseHourly}
                  onChange={(e) => setDraft({ ...draft, baseHourly: e.target.value })}
                  className="mt-1 h-8 text-sm"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                Cost $/hr (burden)
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.costHourly}
                  onChange={(e) => setDraft({ ...draft, costHourly: e.target.value })}
                  placeholder="Optional"
                  className="mt-1 h-8 text-sm"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                OT multiplier
                <Input
                  type="number"
                  min={1}
                  step="0.1"
                  value={draft.otMultiplier}
                  onChange={(e) => setDraft({ ...draft, otMultiplier: e.target.value })}
                  className="mt-1 h-8 text-sm"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                OT weekly threshold
                <Input
                  type="number"
                  min={1}
                  value={draft.otWeeklyThreshold}
                  onChange={(e) => setDraft({ ...draft, otWeeklyThreshold: e.target.value })}
                  className="mt-1 h-8 text-sm"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                Commission %
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  value={draft.commissionPct}
                  onChange={(e) => setDraft({ ...draft, commissionPct: e.target.value })}
                  placeholder="Optional"
                  className="mt-1 h-8 text-sm"
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              Active
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  create.isPending ||
                  update.isPending ||
                  !draft.name.trim() ||
                  draft.baseHourly.trim() === ''
                }
                onClick={save}
              >
                Save
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payroll profile?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" will be removed and unassigned from any technicians. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting) remove.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
